import { ec, ecId, ecLean, handBack, record, type JanusCtx, type Row } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { applyResourceValues } from '../resources/resource-adjust';
import { emptySpawnPlan, mergeSpawnPlans, spawnRowIdentity, type SpawnRowPlan } from '../spawn/row-plan';
import { getSwarmTemplateForCreature, swarmMemberFields } from '../../shared/swarm-templates';
import { planSwarmPile, corpseTemplateId } from './corpse';
import type { DeathResources } from './compose';

const P = 'encounter_creature_swarm_state_';
export function isSwarm(target: Row): boolean {
    return Number(ecLean(target, 'swarm_members_start') ?? 0) >= 2
        || !!getSwarmTemplateForCreature(target as Parameters<typeof getSwarmTemplateForCreature>[0]);
}

/** Fixed full-strength basis: shrinking the maximum must never kill another member on replay. */
export function swarmAttrition(members: number, start: number, current: number, maximum: number, basis: number | null, final = false) {
    const vStart = basis && basis > 0 ? basis : Math.max(1, maximum * start / Math.max(1, members));
    const survivors = final ? 0 : current > 0 ? Math.max(1, Math.min(members, Math.ceil(start * current / vStart - 1e-9))) : members;
    return { members: survivors, delta: Math.max(0, members - survivors), vStart,
        maximum: final ? maximum : Math.max(Math.ceil(current), Math.ceil(vStart * survivors / start - 1e-9)) };
}

function stateFor(ctx: JanusCtx, target: Row, resources: DeathResources): Row | null {
    const selected = target.encounter_creature_swarm_states;
    if (Array.isArray(selected)) return selected.find(row => record(row)
        && String(readItemField(row, P + 'encounter_creature_id')).toUpperCase() === ecId(target)
        && Number(readItemField(row, P + 'deleted') ?? 0) === 0) as Row | undefined ?? null;
    const state = resources.swarmState?.(ctx, target);
    if (state === undefined) handBack('swarm-shed:state-rows-not-held');
    return state;
}

/** Called before dice. Lanes without the typed row saver/resources refuse the entire action. */
export function requireSwarmShedding(ctx: JanusCtx, target: Row): DeathResources | null {
    if (!isSwarm(target)) {
        if (['swarm_width_lanes', 'swarm', 'swarm_size'].some(key => ctx.read.has(target, key))) handBack('swarm-shed:member-count-not-held');
        return null;
    }
    if (!(Number(ecLean(target, 'swarm_members_start')) >= 2) || !(Number(ecLean(target, 'swarm_members')) >= 0))
        handBack('swarm-shed:member-count-not-held');
    const resources = ctx.read.deathResources?.(ctx, target);
    if (!resources || resources.swarmShedding === false) handBack('swarm-shed:row-save-not-supported');
    stateFor(ctx, target, resources);
    resources.inventory.item(corpseTemplateId(target, ctx.read));
    if (!resources.creatureSource?.(String(ecLean(target, 'item_id')))) handBack('swarm-shed:individual-source-not-held');
    return resources;
}

/** One state row, one attrition maximum, one floor pile in the action's existing transaction. */
export function shedSwarm(ctx: JanusCtx, target: Row, resources: DeathResources, final = false): SpawnRowPlan {
    if (resources.swarmShedding === false) handBack('swarm-shed:row-save-not-supported');
    const plan = emptySpawnPlan();
    const saved = stateFor(ctx, target, resources), members = Number(saved ? readItemField(saved, P + 'members') : ecLean(target, 'swarm_members'));
    const start = Number(saved ? readItemField(saved, P + 'members_start') : ecLean(target, 'swarm_members_start'));
    if (!Number.isFinite(members) || !Number.isFinite(start) || start < 2) handBack('swarm-shed:member-count-not-held');
    if (members <= 0 || Number(ecLean(target, 'encounter_creature_deleted') ?? 0)) return plan;
    const notes = readItemField(saved, P + 'notes');
    let basis: number | null = null;
    try { basis = Number(record(typeof notes === 'string' ? JSON.parse(notes) : notes)?.v_start) || null; } catch { /* old rows reconstruct once */ }
    const current = Number(ecLean(target, 'v_current') ?? 0), maximum = Number(ecLean(target, 'v_max') ?? 0);
    const next = swarmAttrition(members, start, current, maximum, basis, final);
    if (!next.delta || ctx.dryRun) return plan;
    const id = ecId(target), revision = Number(readItemField(saved, P + 'revision') ?? 0);
    const row: Row = { [P + 'id']: saved ? readItemField(saved, P + 'id') : spawnRowIdentity('swarm-state|' + id),
        [P + 'members']: next.members, [P + 'members_start']: start,
        [P + 'shed_total']: Number(readItemField(saved, P + 'shed_total') ?? 0) + next.delta,
        [P + 'revision']: revision + 1, [P + 'notes']: JSON.stringify({ v_start: next.vStart }), [P + 'deleted']: 0 };
    if (!saved) row[P + 'encounter_creature_id'] = id;
    const individual = resources.creatureSource?.(String(ecLean(target, 'item_id')));
    if (!individual) handBack('swarm-shed:individual-source-not-held');
    const pile = planSwarmPile(target, individual, next.delta, ctx.read, resources.inventory);
    const pendingSeed = ctx.spawnRows?.writes.find(write => write.table === 'encounter_creature_swarm_states'
        && write.op === 'insert' && readItemField(write.row, P + 'id') === readItemField(row, P + 'id'));
    // A previous hit seeded this action's unsaved ledger; its one INSERT owns the final state.
    if (pendingSeed) Object.assign(pendingSeed.row, row);
    else plan.writes.push({ table: 'encounter_creature_swarm_states', op: saved ? 'update' : 'insert', row,
        ...(saved ? { before: { [P + 'revision']: revision, [P + 'encounter_creature_id']: id, [P + 'deleted']: 0 } } : {}) });
    if (next.members === 1) plan.logs!.push({ kind: 'janus_unported_skip', actor_id: id,
        name: String(ecLean(target, 'encounter_creature_name') ?? 'Swarm'),
        detail: 'The last swarm member survives; inherited swarm trait retirement is not yet supported. Member count, width and bodies are saved.' });
    const full = { ...saved, ...row, [P + 'encounter_creature_id']: id };
    target.encounter_creature_swarm_states = [full];
    Object.assign(ec(target), swarmMemberFields(next.members, start));
    if (!final) applyResourceValues(target, { v_max: next.maximum });
    return mergeSpawnPlans(plan, pile);
}

export function shedSwarmAfterDamage(ctx: JanusCtx, target: Row): void {
    if (!isSwarm(target) || Number(ecLean(target, 'v_current') ?? 0) <= 0 || ctx.composedDeaths?.includes(ecId(target))) return;
    const resources = requireSwarmShedding(ctx, target)!;
    const plan = shedSwarm(ctx, target, resources);
    if (plan.writes.length) ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, resources.prepareRows?.(plan) ?? plan);
}
